import { onMounted, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { detectLocale, persistLocale } from '../i18n/index'
import { DEFAULT_LOCALE, localeFromPath, localizedPath } from '../i18n/routing'

/**
 * Keeps the visible language and the address bar in agreement.
 *
 * Why this exists: each locale is a real, separately crawlable URL, so the page's
 * language must always be readable from its path. Three cases to handle:
 *
 *  - **Arriving.** If the URL carries no locale prefix but the visitor prefers
 *    another language, we switch and `replaceState` so the URL matches what is on
 *    screen — without adding a history entry for something the user never did.
 *  - **Switching.** `LanguageSwitcher` pushes the new URL; we follow it here.
 *  - **Back / forward.** `popstate` moves between locale URLs, so re-read the path.
 *
 * Navigation is always History API, never a reload: the translations are already
 * in memory, so switching stays instant.
 */
export function useLocaleRouting(): void {
  const { locale } = useI18n()

  function setLocale(next: string): void {
    if (next !== locale.value) locale.value = next
    persistLocale(next)
  }

  /**
   * Arriving: no prefix means "unspecified", so infer from preference or browser.
   */
  function applyOnArrival(): void {
    setLocale(detectLocale())
  }

  /**
   * Navigating: the user moved to a specific URL, so read it literally. A bare path
   * *is* the default locale's page — falling back to a stored preference here would
   * leave `/` rendering Portuguese, with the URL and the content disagreeing.
   */
  function applyFromHistory(): void {
    const path = typeof window === 'undefined' ? '/' : window.location.pathname
    setLocale(localeFromPath(path) ?? DEFAULT_LOCALE)
  }

  function alignUrl(replace: boolean): void {
    if (typeof window === 'undefined') return
    const target = localizedPath(window.location.pathname, locale.value)
    if (target === window.location.pathname) return
    // The design lives in the fragment and the query may carry campaign tags —
    // losing either on a language switch would break shared links.
    const url = `${target}${window.location.search}${window.location.hash}`
    if (replace) window.history.replaceState(window.history.state, '', url)
    else window.history.pushState(null, '', url)
  }

  function onPopState(): void {
    applyFromHistory()
  }

  onMounted(() => {
    applyOnArrival()
    alignUrl(true)
    window.addEventListener('popstate', onPopState)
  })

  onUnmounted(() => {
    if (typeof window !== 'undefined') window.removeEventListener('popstate', onPopState)
  })

  // A switch made anywhere in the app updates the URL, not just the switcher.
  watch(locale, () => {
    persistLocale(locale.value)
    alignUrl(false)
  })
}
